import { useState, useCallback } from 'react';
import {
  Container,
  Stepper,
  Title,
  Stack,
  Modal,
  Text,
  Group,
  Button,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconUpload,
  IconColumns,
  IconChecklist,
  IconCircleCheck,
} from '@tabler/icons-react';
import type { ImportWizardState } from './types';
import { UploadStep } from './steps/UploadStep';
import { ColumnMappingStep } from './steps/ColumnMappingStep';
import { ReviewStep } from './steps/ReviewStep';
import { CompleteStep } from './steps/CompleteStep';
import { useBulkImport, useBulkPreflight } from './hooks/useBulkImport';
import type { BulkImportRequest } from './hooks/useBulkImport';

const INITIAL_STATE: ImportWizardState = {
  step: 0,
  livestockType: 'coral',
  currency: 'USD',
  parsedData: null,
  columnMappings: [],
  dateFormat: null,
  importRows: [],
  fileName: null,
  importResult: null,
  reviewSourceSignature: null,
  hasReviewEdits: false,
};

const REVIEW_AFFECTING_KEYS: ReadonlySet<keyof ImportWizardState> = new Set([
  'parsedData',
  'columnMappings',
  'dateFormat',
  'livestockType',
]);

function computeReviewSourceSignature(state: ImportWizardState): string {
  const { parsedData, columnMappings, dateFormat, livestockType } = state;
  const dataProxy = parsedData
    ? {
        headers: parsedData.headers,
        rowCount: parsedData.rows.length,
        sheet: parsedData.selectedSheet,
      }
    : null;
  const mappingProxy = columnMappings.map((m) => ({
    source: m.sourceColumn,
    target: m.targetField,
  }));
  return JSON.stringify({
    data: dataProxy,
    mappings: mappingProxy,
    dateFormat,
    livestockType,
  });
}

export function BulkImportWizard() {
  const [state, setState] = useState<ImportWizardState>(INITIAL_STATE);
  const [pendingBackStep, setPendingBackStep] = useState<number | null>(null);
  const bulkImport = useBulkImport();
  const bulkPreflight = useBulkPreflight();

  const updateState = useCallback(
    (updates: Partial<ImportWizardState>) => {
      setState((prev) => {
        const changesReviewFields = Object.keys(updates).some((k) =>
          REVIEW_AFFECTING_KEYS.has(k as keyof ImportWizardState),
        );
        if (changesReviewFields && prev.importRows.length > 0) {
          return {
            ...prev,
            ...updates,
            importRows: [],
            importResult: null,
            reviewSourceSignature: null,
            hasReviewEdits: false,
          };
        }
        return { ...prev, ...updates };
      });
    },
    [],
  );

  const goToStep = useCallback(
    (targetStep: number) => {
      if (state.step === 2 && targetStep < 2 && state.hasReviewEdits) {
        setPendingBackStep(targetStep);
        return;
      }

      setState((prev) => {
        if (targetStep === 2 && prev.importRows.length > 0) {
          const sig = computeReviewSourceSignature(prev);
          if (sig !== prev.reviewSourceSignature) {
            return {
              ...prev,
              step: targetStep,
              importRows: [],
              importResult: null,
              reviewSourceSignature: sig,
              hasReviewEdits: false,
            };
          }
          return { ...prev, step: targetStep };
        }
        if (targetStep === 2 && prev.importRows.length === 0) {
          return {
            ...prev,
            step: targetStep,
            reviewSourceSignature: computeReviewSourceSignature(prev),
          };
        }
        return { ...prev, step: targetStep };
      });
    },
    [state.step, state.hasReviewEdits],
  );

  const confirmBackNavigation = useCallback(() => {
    if (pendingBackStep === null) return;
    const targetStep = pendingBackStep;
    setPendingBackStep(null);
    setState((prev) => ({
      ...prev,
      step: targetStep,
      importRows: [],
      importResult: null,
      reviewSourceSignature: null,
      hasReviewEdits: false,
    }));
  }, [pendingBackStep]);

  const handleImport = useCallback(
    (request: BulkImportRequest) => {
      bulkImport.mutate(request, {
        onSuccess: (result) => {
          updateState({ importResult: result, step: 3 });
        },
      });
    },
    [bulkImport, updateState],
  );

  const handlePreflight = useCallback(
    (request: BulkImportRequest) => bulkPreflight.mutateAsync(request),
    [bulkPreflight],
  );

  const handleReset = () => {
    bulkImport.reset();
    bulkPreflight.reset();
    setState(INITIAL_STATE);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Title order={1}>Import Livestock</Title>

        <Stepper
          active={state.step}
          onStepClick={goToStep}
          allowNextStepsSelect={false}
        >
          <Stepper.Step label="Upload" icon={<IconUpload size={18} />}>
            <UploadStep
              state={state}
              onUpdate={updateState}
              onNext={() => goToStep(1)}
            />
          </Stepper.Step>

          <Stepper.Step label="Map Columns" icon={<IconColumns size={18} />}>
            <ColumnMappingStep
              state={state}
              onUpdate={updateState}
              onNext={() => goToStep(2)}
              onBack={() => goToStep(0)}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Review & Edit"
            icon={<IconChecklist size={18} />}
          >
            <ReviewStep
              state={state}
              onUpdate={updateState}
              onImport={handleImport}
              onPreflight={handlePreflight}
              onBack={() => goToStep(1)}
              isImporting={bulkImport.isPending || bulkPreflight.isPending}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Complete"
            icon={<IconCircleCheck size={18} />}
          >
            <CompleteStep
              state={state}
              importResult={state.importResult}
              onReset={handleReset}
            />
          </Stepper.Step>
        </Stepper>
      </Stack>

      <Modal
        opened={pendingBackStep !== null}
        onClose={() => setPendingBackStep(null)}
        title={
          <Group gap="xs">
            <IconAlertTriangle
              size={20}
              color="var(--mantine-color-yellow-6)"
            />
            <Text fw={600}>Discard review edits?</Text>
          </Group>
        }
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Going back will discard your review edits and reprocess rows from
            your spreadsheet.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setPendingBackStep(null)}>
              Stay
            </Button>
            <Button color="yellow" onClick={confirmBackNavigation}>
              Go Back
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
